import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';

import rainbowChestImage from 'assets/png/daily-quests/rainbow-chest.png?format=webp;avif&as=picture';

import { CountdownBox } from './components/countdown-box/countdown-box';
import { RoundAnimation } from './components/round-animation/round-animation';
import { RoundResult } from './components/round-result/round-result';
import { DEFAULT_CREDITS_ATTEMPTS } from './prize-box-page.constants';
import styles from './prize-box-page.module.css';
import { GamePhase, IPrizeBoxMainContentProps } from './prize-box-page.types';
import { ChestArea, DescriptionBlock, HeaderPool } from './prize-box-page.utils';
import { formatUsd } from './services/units';

export const PrizeBoxMainContent: FC<IPrizeBoxMainContentProps> = ({
  loading,
  pool,
  phase,
  attemptsLeft,
  points,
  snapshotPoints,
  playerWins,
  aiWins,
  lastMoves,
  selectedMove,
  aiMove,
  lastRoundOutcome,
  quotaResetAt,
  extraCredits,
  recoveryAmount,
  result,
  closeResult,
  selectedMoveTopHand,
  error,
  currentSlotBalance,
}) => {
  const { t } = useTranslation();
  return (
    <div className="z-50 flex flex-1 flex-col">
      {loading ? (
        <div className={clsx(styles.loading, 'flex flex-1 items-center justify-center')}>
          <p className="absolute inset-0 flex items-center justify-center text-3xl font-black text-white/75">
            {t('prizeBox.loading')}
          </p>
          <OptimizedImage
            imageData={rainbowChestImage}
            alt="chest"
            className="mx-auto h-full w-full max-w-64 animate-pulse object-cover opacity-30"
          />
        </div>
      ) : (
        <div className="mx-auto flex w-full max-w-screen-xs flex-1 flex-col overflow-hidden pb-0 pt-2 sm:max-w-screen-sm">
          <HeaderPool label={t('prizeBox.pool')} poolCents={pool} />
          {phase !== GamePhase.Result && attemptsLeft > 0 && (
            <div>
              <InfiniteMaxProgressBar
                value={Number(points)}
                className="mx-auto mb-2 max-w-72"
                showAttention
              />
              <DescriptionBlock points={Number(points)} />
            </div>
          )}
          <div className="flex flex-1 flex-col justify-between gap-6 sm:mt-10">
            {phase === GamePhase.Result && lastMoves && (
              <RoundAnimation
                phase={phase}
                pointBalance={Number(points)}
                snapshotPoints={Number(snapshotPoints)}
                amIPlayerA
                playerA={{ wins: playerWins }}
                playerB={{ wins: aiWins }}
                lastMoves={
                  lastMoves ?? {
                    playerAMove: selectedMove!,
                    playerBMove: aiMove!,
                  }
                }
                lastRoundOutcome={lastRoundOutcome}
              />
            )}
            {phase !== GamePhase.Result && attemptsLeft > 0 && (
              <ChestArea
                phase={phase}
                tConfirming={t('prizeBox.confirming')}
                attemptsLeft={attemptsLeft}
              />
            )}
            {phase !== GamePhase.Result && attemptsLeft === 0 && (
              <>
                <div />
                <CountdownBox
                  quotaResetAt={quotaResetAt}
                  attempts={attemptsLeft}
                  extraCredits={extraCredits}
                />
                <div className={styles.info} data-testid="countdown-section">
                  <p>
                    <Trans
                      i18nKey="prizeBox.buyChest"
                      values={{
                        amount: DEFAULT_CREDITS_ATTEMPTS,
                        price: formatUsd(recoveryAmount, 0),
                      }}
                      components={[<b />, <span />]}
                    />
                  </p>
                </div>
              </>
            )}
            {phase === GamePhase.Result && (
              <RoundResult
                result={result}
                pointsAfter={Number(points)}
                attemptsLeft={attemptsLeft}
                onClose={closeResult}
                currentSlotBalance={currentSlotBalance}
                topHand={selectedMoveTopHand}
              />
            )}
            {error && (
              <div className="py-2 text-center text-md leading-3 text-[#ED5264] sm:text-[14px] sm:leading-4">
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
